import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Per-lesson progress for the guided tutorial.
 *
 * The tutorial already remembered that *a* walkthrough had been finished, but
 * not which of the interactive examples it was — so nothing could tell a
 * learner which lessons they still had left. This records progress per lesson
 * instead.
 *
 * Each completion is written to three places, one per reader: localStorage so
 * the UI can badge finished lessons without waiting on a round trip, GA4 so the
 * lessons sit in the same funnel as the rest of onboarding, and the user's
 * Firestore document so completion can be queried across accounts. Only the
 * first is load-bearing — a signed-out learner with analytics blocked must
 * still get a working tutorial, so the other two fail quietly.
 */

const STORAGE_KEY = 'svgtrace_tutorial_lessons';

const readAll = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeAll = (lessons) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lessons));
  } catch {
    // Private browsing and blocked storage both land here. Progress is a
    // convenience, not something worth interrupting the lesson over.
  }
};

const track = (event, params) => {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', event, { event_category: 'Tutorial', ...params });
    }
  } catch {
    // An analytics failure must never surface to the learner.
  }
};

const persist = (uid, lessonId, entry) => {
  if (!uid) return;

  // Merged rather than replaced so a lesson finished in another tab — or on
  // another device — isn't dropped from the map.
  setDoc(
    doc(db, 'users', uid),
    { tutorialProgress: { [lessonId]: entry } },
    { merge: true },
  ).catch((error) => {
    console.warn('Tutorial: could not save lesson progress', error?.message);
  });
};

/** Everything known about every lesson, keyed by lesson id. */
export const readLessonProgress = readAll;

/** Ids of lessons the learner has finished at least once. */
export const readCompletedLessonIds = () =>
  Object.entries(readAll())
    .filter(([, entry]) => !!entry?.completedAt)
    .map(([lessonId]) => lessonId);

export const recordLessonStarted = ({ uid, lessonId, lessonName, totalSteps }) => {
  if (!lessonId) return;

  const lessons = readAll();
  const previous = lessons[lessonId] || {};

  const entry = {
    ...previous,
    lessonName: lessonName || previous.lessonName || lessonId,
    startedAt: new Date().toISOString(),
    starts: (previous.starts || 0) + 1,
    totalSteps: totalSteps || previous.totalSteps || null,
  };

  lessons[lessonId] = entry;
  writeAll(lessons);

  track('tutorial_lesson_started', {
    event_label: entry.lessonName,
    lesson_id: lessonId,
    attempt: entry.starts,
  });

  persist(uid, lessonId, entry);
};

/**
 * Records how far into a lesson the learner reached. Only the furthest point
 * is kept — stepping backwards to re-read something isn't lost progress, and
 * the useful question is where people stop, not every step they saw.
 */
export const recordLessonStep = ({ uid, lessonId, stepId, stepIndex, totalSteps }) => {
  if (!lessonId || !stepId) return;

  const lessons = readAll();
  const previous = lessons[lessonId];
  if (!previous) return;

  track('tutorial_step_viewed', {
    event_label: stepId,
    lesson_id: lessonId,
    step_id: stepId,
    step_index: stepIndex,
    total_steps: totalSteps,
  });

  if (previous.furthestStepIndex >= stepIndex) return;

  const entry = {
    ...previous,
    furthestStepId: stepId,
    furthestStepIndex: stepIndex,
    totalSteps: totalSteps || previous.totalSteps || null,
    lastSeenAt: new Date().toISOString(),
  };

  lessons[lessonId] = entry;
  writeAll(lessons);
  persist(uid, lessonId, entry);
};

export const recordLessonCompleted = ({ uid, lessonId, lessonName, totalSteps }) => {
  if (!lessonId) return;

  const lessons = readAll();
  const previous = lessons[lessonId] || {};

  const entry = {
    ...previous,
    lessonName: lessonName || previous.lessonName || lessonId,
    completedAt: new Date().toISOString(),
    completions: (previous.completions || 0) + 1,
    totalSteps: totalSteps || previous.totalSteps || null,
  };

  lessons[lessonId] = entry;
  writeAll(lessons);

  track('tutorial_lesson_completed', {
    event_label: entry.lessonName,
    lesson_id: lessonId,
    completions: entry.completions,
    total_steps: entry.totalSteps,
  });

  persist(uid, lessonId, entry);
};
